/**
 * User Profile Popover Component
 * Click on a user avatar or name to see profile info and quick actions
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { User } from '@/types/api.types';
import { useAppStore } from '@/store/app.store';
import { useCreateDMThread } from '@/hooks/useDMs';
import { formatLastSeen, statusDotColor } from '@/utils/presence.utils';
import { usePresenceStore } from '@/store/presence.store';
import Avatar from '@/components/common/Avatar';

interface UserProfilePopoverProps {
  user: User;
  children: React.ReactNode;
}

const UserProfilePopover = ({ user, children }: UserProfilePopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const { setActiveDMThread } = useAppStore();
  const createDMThreadMutation = useCreateDMThread();
  const online = usePresenceStore((s) => s.onlineUserIds.has(user.id));
  const customStatus = usePresenceStore((s) => s.userStatuses.get(user.id));

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const popoverWidth = 256; // w-64
      const popoverHeight = 220; // approximate
      const spaceBelow = window.innerHeight - rect.bottom;
      const showAbove = spaceBelow < popoverHeight && rect.top > popoverHeight;

      const top = showAbove ? rect.top - popoverHeight - 8 : rect.bottom + 8;
      let left = rect.left;

      // Keep within viewport horizontally
      if (left + popoverWidth > window.innerWidth - 16) {
        left = window.innerWidth - popoverWidth - 16;
      }
      if (left < 16) left = 16;

      setPosition({ top, left });
    }
    setIsOpen((prev) => !prev);
  }, [isOpen]);

  const handleSendMessage = async () => {
    try {
      const thread = await createDMThreadMutation.mutateAsync(user.id);
      setActiveDMThread(thread.id);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to open DM:', error);
    }
  };

  return (
    <>
      <div
        ref={triggerRef}
        onClick={handleToggle}
        className="cursor-pointer hover:opacity-80 transition-opacity"
      >
        {children}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            className="fixed z-50 w-64 bg-white dark:bg-dark-bg-elevated border border-gray-200 dark:border-dark-border-primary rounded-xl shadow-lg p-4"
            style={{ top: position.top, left: position.left }}
          >
            {/* User info */}
            <div className="flex flex-col items-center text-center mb-3">
              <Avatar user={user} size="lg" />
              <p className="mt-2 font-semibold text-gray-900 dark:text-dark-text-primary">
                {user.display_name}
              </p>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted">{user.email}</p>
              <div className="flex items-center gap-1.5 mt-1 text-sm">
                <div
                  className={`w-2 h-2 rounded-full ${statusDotColor(online, customStatus)}`}
                />
                <span className="text-gray-600 dark:text-dark-text-secondary">
                  {formatLastSeen(user.last_seen)}
                </span>
              </div>
            </div>

            {/* Action */}
            <button
              onClick={handleSendMessage}
              disabled={createDMThreadMutation.isPending}
              className="w-full py-2 px-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {createDMThreadMutation.isPending ? 'Отваряне...' : 'Изпрати съобщение'}
            </button>
          </div>,
          document.body
        )}
    </>
  );
};

export default UserProfilePopover;
